import { useState, useEffect, useCallback } from 'react'
import { fetchEventsList, deleteEvent } from '../api/eventsApi'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'

function formatDate(d) {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function calculatePeriodDates(periodVal) {
  const today = new Date()
  const now = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  switch (String(periodVal)) {
    case '1':
    case 'Current Year': {
      const from = new Date(now.getFullYear(), 0, 1)
      const till = new Date(now.getFullYear(), 11, 31)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '2':
    case 'Current Month': {
      const from = new Date(now.getFullYear(), now.getMonth(), 1)
      const till = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '3':
    case 'Current Week': {
      const day = now.getDay()
      const diff = now.getDate() - day + (day === 0 ? -6 : 1) // Monday
      const from = new Date(now.setDate(diff))
      const till = new Date(from)
      till.setDate(from.getDate() + 6)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '4':
    case 'Last Year': {
      const from = new Date(now.getFullYear() - 1, 0, 1)
      const till = new Date(now.getFullYear() - 1, 11, 31)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '5':
    case 'Last Month': {
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const till = new Date(now.getFullYear(), now.getMonth(), 0)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '6':
    case 'Last Week': {
      const day = now.getDay()
      const diff = now.getDate() - day + (day === 0 ? -6 : 1) - 7
      const from = new Date(now.setDate(diff))
      const till = new Date(from)
      till.setDate(from.getDate() + 6)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '7':
    case 'Next Year': {
      const from = new Date(now.getFullYear() + 1, 0, 1)
      const till = new Date(now.getFullYear() + 1, 11, 31)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '8':
    case 'Next Month': {
      const from = new Date(now.getFullYear(), now.getMonth() + 1, 1)
      const till = new Date(now.getFullYear(), now.getMonth() + 2, 0)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '9':
    case 'Next Week': {
      const day = now.getDay()
      const diff = now.getDate() - day + (day === 0 ? -6 : 1) + 7
      const from = new Date(now.setDate(diff))
      const till = new Date(from)
      till.setDate(from.getDate() + 6)
      return { fromDate: formatDate(from), tillDate: formatDate(till) }
    }
    case '12':
    case 'Today': {
      const dateStr = formatDate(now)
      return { fromDate: dateStr, tillDate: dateStr }
    }
    case '13':
    case 'Yesterday': {
      const yest = new Date(now)
      yest.setDate(now.getDate() - 1)
      const dateStr = formatDate(yest)
      return { fromDate: dateStr, tillDate: dateStr }
    }
    default:
      return { fromDate: '', tillDate: '' }
  }
}

export default function useEventsList() {
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(false)
  const [sortBy, setSortBy] = useState('tDate')
  const [sortOrder, setSortOrder] = useState('desc')

  // Filters
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [courseFilter, setCourseFilter] = useState('All')
  const [batchFilter, setBatchFilter] = useState('All')
  const [periodFilter, setPeriodFilterState] = useState('0')
  const [fromDate, setFromDate] = useState('')
  const [tillDate, setTillDate] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const handlePeriodChange = (val) => {
    setPeriodFilterState(val)
    if (val && val !== '0' && val !== 'Period' && val !== 'All') {
      const computed = calculatePeriodDates(val)
      setFromDate(computed.fromDate)
      setTillDate(computed.tillDate)
    } else {
      setFromDate('')
      setTillDate('')
    }
  }

  useEffect(() => {
    setPage(1)
  }, [companyFilter, branchFilter, courseFilter, batchFilter, fromDate, tillDate])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const filters = {
        company: companyFilter,
        branch: branchFilter,
        course: courseFilter,
        batch: batchFilter,
        fromDate,
        tillDate,
      }
      const res = await fetchEventsList(filters, sortBy, sortOrder)
      setRecords(res.records)
    } catch (e) {
      console.error('Failed to load events', e)
      appToast.error('Failed to load events list.')
    } finally {
      setLoading(false)
    }
  }, [companyFilter, branchFilter, courseFilter, batchFilter, fromDate, tillDate, sortBy, sortOrder])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    const handler = () => loadData()
    window.addEventListener('cloud-events-updated', handler)
    return () => window.removeEventListener('cloud-events-updated', handler)
  }, [loadData])

  const totalCount = records.length
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const pagedRecords = records.slice((page - 1) * pageSize, page * pageSize)
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const rangeEnd = Math.min(totalCount, page * pageSize)

  const goToNextPage = () => {
    setPage((prev) => Math.min(totalPages, prev + 1))
  }

  const goToPreviousPage = () => {
    setPage((prev) => Math.max(1, prev - 1))
  }

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  const handleDelete = async (id) => {
    const ok = await appConfirm({
      title: 'Delete Entry',
      message: 'Are you sure you want to delete this entry?',
      confirmText: 'Delete',
      isDestructive: true,
    })
    if (ok) {
      try {
        await deleteEvent(id)
        appToast.success('Entry deleted successfully')
        loadData()
      } catch (e) {
        console.error('Failed to delete event', e)
        appToast.error('Error deleting entry')
      }
    }
  }

  const handleResetFilters = () => {
    setCompanyFilter('All')
    setBranchFilter('All')
    setCourseFilter('All')
    setBatchFilter('All')
    setPeriodFilterState('0')
    setFromDate('')
    setTillDate('')
    appToast.info('Filters have been reset.')
  }

  const options = {
    companies: [
      { value: 'All', label: 'All Institutes' },
      { value: 'Tez Educational Trust', label: 'Tez Educational Trust' },
      { value: 'Tez Educational School', label: 'Tez Educational School' },
      { value: 'Tolani College Of Commerce', label: 'Tolani College Of Commerce' },
    ],
    branches: [
      { value: 'All', label: 'All Branches' },
      { value: 'Dadar', label: 'Dadar' },
      { value: 'Thane', label: 'Thane' },
      { value: 'Andheri', label: 'Andheri' },
      { value: 'Borivali', label: 'Borivali' },
    ],
    courses: [
      { value: 'All', label: 'All Courses' },
      { value: 'CA Foundation', label: 'CA Foundation' },
      { value: 'CS Executive', label: 'CS Executive' },
      { value: 'JEE Advanced', label: 'JEE Advanced' },
      { value: 'NEET UG', label: 'NEET UG' },
    ],
    batches: [
      { value: 'All', label: 'All Batches' },
      { value: 'Batch-A (Morning)', label: 'Batch-A (Morning)' },
      { value: 'Batch-CS-01', label: 'Batch-CS-01' },
      { value: 'Super-30 (Intensive)', label: 'Super-30 (Intensive)' },
      { value: 'Med-Achievers', label: 'Med-Achievers' },
    ],
    periods: [
      { value: '0', label: 'Period (All)' },
      { value: '1', label: 'Current Year' },
      { value: '2', label: 'Current Month' },
      { value: '3', label: 'Current Week' },
      { value: '4', label: 'Last Year' },
      { value: '5', label: 'Last Month' },
      { value: '6', label: 'Last Week' },
      { value: '7', label: 'Next Year' },
      { value: '8', label: 'Next Month' },
      { value: '9', label: 'Next Week' },
      { value: '12', label: 'Today' },
      { value: '13', label: 'Yesterday' },
    ],
  }

  return {
    state: {
      records,
      pagedRecords,
      loading,
      sortBy,
      sortOrder,
      companyFilter,
      branchFilter,
      courseFilter,
      batchFilter,
      periodFilter,
      fromDate,
      tillDate,
      options,
      totalCount,
      page,
      pageSize,
      totalPages,
      rangeStart,
      rangeEnd,
    },
    actions: {
      setCompanyFilter,
      setBranchFilter,
      setCourseFilter,
      setBatchFilter,
      setPeriodFilter: handlePeriodChange,
      setFromDate,
      setTillDate,
      setPage,
      setPageSize,
      goToNextPage,
      goToPreviousPage,
      handleSort,
      handleDelete,
      handleResetFilters,
      loadData,
    },
  }
}
